'use client'

import * as React from 'react'
import { CheckCircle, XCircle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'components/ui/card'
import { Badge } from 'components/ui/badge'
import { Progress } from 'components/ui/progress'
import { cn } from 'lib/utils'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell } from 'recharts'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table'
import { collection, getDocs } from 'firebase/firestore'
import { db } from '../../../../firebase'

const getComplianceCounts = (map = {}) => {
  const values = Object.values(map).filter((value) => value === 'yes' || value === 'no')
  const yes = values.filter((value) => value === 'yes').length
  const no = values.filter((value) => value === 'no').length
  return { yes, no, total: values.length }
}

export default function CompliancePage() {
  const [entries, setEntries] = React.useState([])
  const [projects, setProjects] = React.useState([])
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    let isMounted = true

    async function loadCompliance() {
      try {
        const [logframeSnap, projectSnap] = await Promise.all([
          getDocs(collection(db, 'projectLogframes')),
          getDocs(collection(db, 'projects')),
        ])
        if (!isMounted) return
        const logframes = logframeSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        const projectList = projectSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        setEntries(logframes)
        setProjects(projectList)
      } catch {
        if (!isMounted) return
        setEntries([])
        setProjects([])
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    loadCompliance()

    return () => {
      isMounted = false
    }
  }, [])

  const projectMap = projects.reduce((acc, project) => {
    acc[project.id] = project
    return acc
  }, {})

  const enrichedEntries = entries.map((entry) => {
    const sdg = getComplianceCounts(entry.sdgCompliance)
    const pillar = getComplianceCounts(entry.strategicPillarCompliance)
    const total = sdg.total + pillar.total
    const yes = sdg.yes + pillar.yes
    const rate = total > 0 ? Math.round((yes / total) * 100) : 0
    return {
      ...entry,
      projectName:
        projectMap[entry.projectId]?.code ||
        projectMap[entry.projectId]?.shortCode ||
        projectMap[entry.projectId]?.title ||
        entry.projectId ||
        'Project',
      sdg,
      pillar,
      total,
      yes,
      rate,
    }
  })

  const totals = enrichedEntries.reduce(
    (acc, entry) => {
      acc.sdgYes += entry.sdg.yes
      acc.sdgNo += entry.sdg.no
      acc.pillarYes += entry.pillar.yes
      acc.pillarNo += entry.pillar.no
      acc.entries += 1
      acc.total += entry.total
      acc.yes += entry.yes
      return acc
    },
    { sdgYes: 0, sdgNo: 0, pillarYes: 0, pillarNo: 0, entries: 0, total: 0, yes: 0 },
  )

  const overallRate = totals.total > 0 ? Math.round((totals.yes / totals.total) * 100) : 0
  const projectIds = new Set(enrichedEntries.map((entry) => entry.projectId))
  const projectsCovered = projectIds.size

  const pieData = [
    { name: 'Compliant (Yes)', value: totals.sdgYes + totals.pillarYes, color: 'oklch(0.55 0.16 150)' },
    { name: 'Not Compliant (No)', value: totals.sdgNo + totals.pillarNo, color: 'oklch(0.577 0.245 27.325)' },
  ]

  const projectStatsMap = new Map()
  enrichedEntries.forEach((entry) => {
    const current = projectStatsMap.get(entry.projectId) || {
      projectId: entry.projectId,
      project: entry.projectName,
      sdgYes: 0,
      sdgNo: 0,
      pillarYes: 0,
      pillarNo: 0,
    }
    current.sdgYes += entry.sdg.yes
    current.sdgNo += entry.sdg.no
    current.pillarYes += entry.pillar.yes
    current.pillarNo += entry.pillar.no
    projectStatsMap.set(entry.projectId, current)
  })

  const projectStats = Array.from(projectStatsMap.values()).map((stat) => {
    const total = stat.sdgYes + stat.sdgNo + stat.pillarYes + stat.pillarNo
    const yes = stat.sdgYes + stat.pillarYes
    const rate = total > 0 ? Math.round((yes / total) * 100) : 0
    return { ...stat, total, yes, rate }
  })

  const sdgBarData = projectStats.map((stat) => ({
    project: stat.project,
    yes: stat.sdgYes,
    no: stat.sdgNo,
  }))

  const pillarBarData = projectStats.map((stat) => ({
    project: stat.project,
    yes: stat.pillarYes,
    no: stat.pillarNo,
  }))

  return (
    <div className="flex flex-col gap-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground font-serif tracking-tight">Compliance Tracker</h1>
        <p className="text-sm text-muted-foreground mt-1">
          SDG alignment and strategic pillar compliance from project M&E entries
        </p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-4xl font-bold text-foreground">{loading ? '—' : `${overallRate}%`}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">Overall Compliance</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-success">{loading ? '—' : totals.sdgYes + totals.pillarYes}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">Compliant (Yes)</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-success" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-destructive">{loading ? '—' : totals.sdgNo + totals.pillarNo}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">Not Compliant (No)</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-destructive" />
          </CardContent>
        </Card>
        <Card className="border border-border relative overflow-hidden">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-foreground">{loading ? '—' : projectsCovered}</p>
            <p className="text-xs text-muted-foreground font-medium mt-1">Projects Covered</p>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-muted-foreground/30" />
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="border border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-foreground">Overall Compliance</CardTitle>
            <CardDescription className="text-xs">Yes vs No across SDGs and pillars</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center">
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={3} strokeWidth={0}>
                  {pieData.map(e => <Cell key={e.name} fill={e.color} />)}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap items-center justify-center gap-3 mt-2">
              {pieData.map(e => (
                <div key={e.name} className="flex items-center gap-1.5">
                  <span className="size-2 rounded-full" style={{ backgroundColor: e.color }} />
                  <span className="text-[10px] text-muted-foreground">{e.name} ({e.value})</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-foreground">SDG Compliance by Project</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={sdgBarData} barSize={18}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.9 0.01 145)" />
                <XAxis dataKey="project" tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                <YAxis tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="yes" stackId="a" fill="oklch(0.55 0.16 150)" name="Yes" />
                <Bar dataKey="no" stackId="a" fill="oklch(0.577 0.245 27.325)" name="No" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-foreground">Strategic Pillar Compliance</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={pillarBarData} barSize={18}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.9 0.01 145)" />
                <XAxis dataKey="project" tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                <YAxis tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="yes" stackId="a" fill="oklch(0.35 0.12 145)" name="Yes" />
                <Bar dataKey="no" stackId="a" fill="oklch(0.577 0.245 27.325)" name="No" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Project Compliance Cards */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {loading ? (
          <Card className="border border-border lg:col-span-2">
            <CardContent className="p-6 text-center text-sm text-muted-foreground">
              Loading compliance data...
            </CardContent>
          </Card>
        ) : projectStats.length === 0 ? (
          <Card className="border border-border lg:col-span-2">
            <CardContent className="p-6 text-center text-sm text-muted-foreground">
              No M&E compliance entries available yet.
            </CardContent>
          </Card>
        ) : (
          projectStats.map((stat) => (
            <Card key={stat.projectId} className="border border-border">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">{stat.project}</h3>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      SDG Yes: {stat.sdgYes} | SDG No: {stat.sdgNo} | Pillar Yes: {stat.pillarYes} | Pillar No: {stat.pillarNo}
                    </p>
                  </div>
                  <Badge
                    className={cn(
                      'text-[10px] shrink-0',
                      stat.rate >= 80
                        ? 'bg-success/15 text-success'
                        : stat.rate >= 50
                        ? 'bg-warning/15 text-warning'
                        : 'bg-destructive/15 text-destructive',
                    )}
                  >
                    {stat.rate}%
                  </Badge>
                </div>
                <Progress value={stat.rate} className="h-2 mb-3" />
                <div className="grid grid-cols-3 gap-2">
                  <div className="flex flex-col items-center gap-0.5 rounded bg-muted/50 p-2">
                    <span className="text-lg font-bold text-foreground">{stat.total}</span>
                    <span className="text-[10px] text-muted-foreground">Total Checks</span>
                  </div>
                  <div className="flex flex-col items-center gap-0.5 rounded bg-success/5 p-2">
                    <CheckCircle className="size-4 text-success" />
                    <span className="text-sm font-bold text-success">{stat.yes}</span>
                    <span className="text-[10px] text-muted-foreground">Yes</span>
                  </div>
                  <div className="flex flex-col items-center gap-0.5 rounded bg-destructive/5 p-2">
                    <XCircle className="size-4 text-destructive" />
                    <span className="text-sm font-bold text-destructive">{stat.total - stat.yes}</span>
                    <span className="text-[10px] text-muted-foreground">No</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle className="text-sm font-semibold text-foreground">M&E Compliance Entries</CardTitle>
          <CardDescription className="text-xs">Each M&E logframe entry with SDG and pillar compliance</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Project</TableHead>
                  <TableHead className="text-xs">Level</TableHead>
                  <TableHead className="text-xs">Statement</TableHead>
                  <TableHead className="text-xs">SDG Yes/No</TableHead>
                  <TableHead className="text-xs">Pillar Yes/No</TableHead>
                  <TableHead className="text-xs">Compliance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                      Loading entries...
                    </TableCell>
                  </TableRow>
                ) : enrichedEntries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                      No M&E logframe entries found.
                    </TableCell>
                  </TableRow>
                ) : (
                  enrichedEntries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="text-xs text-muted-foreground">{entry.projectName}</TableCell>
                      <TableCell className="text-xs">{entry.level || '—'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[220px] truncate">
                        {entry.statement || '—'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {entry.sdg.yes}/{entry.sdg.no}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {entry.pillar.yes}/{entry.pillar.no}
                      </TableCell>
                      <TableCell className="text-xs">
                        <Badge
                          className={cn(
                            'text-[10px]',
                            entry.rate >= 80
                              ? 'bg-success/15 text-success'
                              : entry.rate >= 50
                              ? 'bg-warning/15 text-warning'
                              : 'bg-destructive/15 text-destructive',
                          )}
                        >
                          {entry.rate}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
